import { useEffect, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import ReactSelect from 'react-select';
import styled from 'styled-components';
import {
  InputText,
  PrimaryButton,
  SecondaryButton,
  Select,
} from '../../../shared/ui/Inputs';
import { ErrorMessage } from '../../../shared/messages/messages';
import {
  createUser,
  editUser,
} from '../../../services/configuration/userManagementService';
import { listCompanies } from '../../../services/configuration/companiesService';
import {
  normalizePatientV2Profile,
  PATIENT_V2_PROFILE_OPTIONS,
} from '../../patients/usecases/v2/types';

const SelectedCompaniesWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
`;

const CompanyTag = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  background-color: #f1f3f4;
  border: 1px solid #dadce0;
  color: #5f6368;
  transition: background-color 0.15s ease;
  &:hover {
    background-color: #e8eaed;
  }
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  color: inherit;
  opacity: 0.6;
  font-size: 14px;
  line-height: 1;
  &:hover {
    opacity: 1;
  }
`;

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '38px',
    borderColor: state.isFocused ? '#4285f4' : '#d9d9d9',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(66,133,244,0.15)' : 'none',
    '&:hover': { borderColor: '#4285f4' },
    borderRadius: '4px',
    fontSize: '14px',
  }),
  option: (base, state) => ({
    ...base,
    fontSize: '13px',
    backgroundColor: state.isSelected
      ? '#e8f0fe'
      : state.isFocused
      ? '#f8f9fa'
      : 'white',
    color: state.isSelected ? '#1a73e8' : '#333',
    cursor: 'pointer',
    padding: '8px 12px',
  }),
  multiValue: () => ({ display: 'none' }),
  multiValueLabel: () => ({ display: 'none' }),
  multiValueRemove: () => ({ display: 'none' }),
  placeholder: (base) => ({ ...base, color: '#aaa', fontSize: '13px' }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const SectionTitle = ({ children }) => (
  <h3
    style={{
      fontSize: '16px',
      fontWeight: 600,
      color: '#444',
      marginBottom: '15px',
      paddingBottom: '8px',
      borderBottom: '1px solid #e8e8e8',
    }}
  >
    {children}
  </h3>
);

const FieldLabel = ({ children }) => (
  <label
    style={{
      display: 'block',
      marginBottom: '6px',
      fontSize: '13px',
      fontWeight: 500,
      color: '#555',
    }}
  >
    {children}
  </label>
);

export const UserForm = ({
  id,
  name,
  email,
  cnpj,
  isSpecialist,
  isGeneralist,
  isManager,
  isWhiteMartins,
  canSeeAllCnpjs,
  isAdmin,
  patientV2Profile,
  accessImplantacao,
  accessAssistencia,
  accessRecarga,
  accessRecolhimento,
  accessVenda,
  accessSubalocacao,
  accessReport,
  canEditPaymentList,
  onAction,
  onCancel,
  onSaved,
  isEditing,
  // companies vem do listUsers: [{ cnpj, company_name, ... }]
  companies = [],
}) => {
  const isTabletOrMobile = useMediaQuery({ query: '(max-width: 1224px)' });

  const [_name, setName] = useState(name ?? '');
  const [_email, setEmail] = useState(email ?? '');
  const [roleLevel, setRoleLevel] = useState({
    isSpecialist,
    isGeneralist,
    isManager,
    isWhiteMartins,
    isAdmin,
  });
  const [patientV2ProfileValue, setPatientV2ProfileValue] = useState(() =>
    normalizePatientV2Profile(patientV2Profile, isAdmin)
  );
  const [permissions, setPermissions] = useState({
    implantacao: accessImplantacao === '1' || accessImplantacao === true,
    assistencia: accessAssistencia === '1' || accessAssistencia === true,
    recarga:
      accessRecarga === '1' ||
      accessRecarga === true ||
      accessRecarga === undefined,
    recolhimento: accessRecolhimento === '1' || accessRecolhimento === true,
    venda: accessVenda === '1' || accessVenda === true,
    subalocacao: accessSubalocacao === '1' || accessSubalocacao === true,
    report: accessReport === '1' || accessReport === true,
    paymentList: canEditPaymentList === '1' || canEditPaymentList === true,
  });
  const [canSeeAllCnpjsValue, setCanSeeAllCnpjsValue] = useState(
    isWhiteMartins === '1' &&
      (canSeeAllCnpjs === undefined ||
        canSeeAllCnpjs === null ||
        canSeeAllCnpjs === '' ||
        canSeeAllCnpjs === '1' ||
        canSeeAllCnpjs === true)
  );
  const [message, setMessage] = useState('');
  const [allCompanies, setAllCompanies] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]);

  const toOption = (c) => ({
    value: c.id,
    label: `${c.empresa} — ${c.cnpj}`,
    cnpj: c.cnpj,
    empresa: c.empresa,
  });

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const list = await listCompanies();
        setAllCompanies(list);

        if (isEditing && companies.length > 0) {
          // Match por cnpj — formato usercompany: { cnpj, company_name }
          const linked = companies
            .map((link) => list.find((c) => c.cnpj === link.cnpj))
            .filter(Boolean)
            .map(toOption);
          setSelectedCompanies(linked);
        } else if (cnpj) {
          // Fallback: apenas o cnpj principal do usuário
          const found = list.find((c) => c.cnpj === cnpj);
          if (found) setSelectedCompanies([toOption(found)]);
        }
      } catch (err) {
        console.error('Failed to fetch companies:', err);
      }
    };
    fetchCompanies();
  }, []);

  const companyOptions = allCompanies.map(toOption);

  const handleSelectCompany = (option) => {
    if (!option) return;
    if (selectedCompanies.some((c) => c.value === option.value)) return;
    setSelectedCompanies((prev) => [...prev, option]);
  };

  const handleRemoveCompany = (companyId) => {
    setSelectedCompanies((prev) => prev.filter((c) => c.value !== companyId));
  };

  const setRole = (value) => {
    const reset = {};
    Object.keys(roleLevel).forEach((k) => (reset[k] = '0'));
    reset[value] = '1';
    setRoleLevel(reset);
    // No cadastro, o perfil geral sugere o perfil V2. Na edição,
    // preserva o perfil V2 salvo independentemente do perfil geral.
    if (!isEditing) {
      setPatientV2ProfileValue(
        normalizePatientV2Profile(undefined, reset.isAdmin)
      );
    }
    if (value !== 'isWhiteMartins') {
      setCanSeeAllCnpjsValue(false);
    }
    if (value === 'isAdmin') {
      setPermissions({
        implantacao: true,
        assistencia: true,
        recarga: true,
        recolhimento: true,
        venda: true,
        subalocacao: true,
        report: true,
        paymentList: true,
      });
    } else {
      setPermissions({
        implantacao: false,
        assistencia: false,
        recarga: false,
        recolhimento: false,
        venda: false,
        subalocacao: false,
        report: false,
        paymentList: false,
      });
    }
  };

  const saveUser = async () => {
    if (
      !_email ||
      /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(_email) === false
    ) {
      setMessage('Você precisa informar um e-mail válido');
      return;
    }
    if (!_name) {
      setMessage('Você precisa informar um nome válido');
      return;
    }
    if (selectedCompanies.length === 0) {
      setMessage('Você precisa selecionar ao menos uma empresa');
      return;
    }

    const user = {
      id,
      name: _name,
      email: _email,
      cnpj: selectedCompanies[0].cnpj,
      companyName: selectedCompanies[0].empresa,
      cnpjs: selectedCompanies.map((c) => c.cnpj),
      companies: selectedCompanies.map((c) => ({
        id: c.value,
        cnpj: c.cnpj,
        empresa: c.empresa,
      })),
      isSpecialist: roleLevel.isSpecialist ?? '0',
      isGeneralist: roleLevel.isGeneralist ?? '0',
      isManager: roleLevel.isManager ?? '0',
      isWhiteMartins: roleLevel.isWhiteMartins ?? '0',
      canSeeAllCnpjs:
        roleLevel.isWhiteMartins === '1' && canSeeAllCnpjsValue ? '1' : '0',
      isAdmin: roleLevel.isAdmin ?? '0',
      patientV2Profile: patientV2ProfileValue,
      accessImplantacao: permissions.implantacao ? '1' : '0',
      accessAssistencia: permissions.assistencia ? '1' : '0',
      accessRecarga: permissions.recarga ? '1' : '0',
      accessRecolhimento: permissions.recolhimento ? '1' : '0',
      accessVenda: permissions.venda ? '1' : '0',
      accessSubalocacao: permissions.subalocacao ? '1' : '0',
      accessReport: permissions.report ? '1' : '0',
      canEditPaymentList:
        roleLevel.isAdmin === '1' ||
        ((roleLevel.isManager === '1' || roleLevel.isWhiteMartins === '1') &&
          permissions.paymentList)
          ? '1'
          : '0',
    };

    if (isEditing) {
      await editUser(user);
    } else {
      await createUser(user);
    }

    if (typeof onSaved === 'function') onSaved();
    else if (typeof onAction === 'function') onAction();
  };

  return (
    <div style={{ position: 'relative', height: '100%' }}>
      {message !== '' && (
        <div
          style={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 2 }}
        >
          <ErrorMessage>{message}</ErrorMessage>
        </div>
      )}

      <div
        style={{
          margin: 0,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          paddingTop: message !== '' ? '60px' : 0,
        }}
      >
        <div style={{ flex: 1, overflowY: 'auto', paddingRight: '5px' }}>
          {/* ── Informações Básicas ── */}
          <div style={{ marginBottom: '25px' }}>
            <SectionTitle>Informações Básicas</SectionTitle>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: isTabletOrMobile ? '1fr' : '1fr 1fr',
                gap: '15px',
              }}
            >
              <div>
                <FieldLabel>Nome Completo *</FieldLabel>
                <InputText
                  data-testid="user-form-name"
                  value={_name}
                  placeholder="Digite o nome completo"
                  style={{ width: '100%' }}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div>
                <FieldLabel>Email {isEditing && '(não editável)'} *</FieldLabel>
                <InputText
                  data-testid="user-form-email"
                  disabled={isEditing}
                  value={_email}
                  placeholder="Digite o email"
                  style={{
                    width: '100%',
                    backgroundColor: isEditing ? '#f5f5f5' : 'white',
                  }}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* ── Empresas Vinculadas ── */}
          <div style={{ marginBottom: '25px' }}>
            <SectionTitle>Empresas Vinculadas</SectionTitle>
            <FieldLabel>Selecione as empresas *</FieldLabel>
            <ReactSelect
              inputId="user-company-select"
              instanceId="user-company-select"
              options={companyOptions}
              value={null}
              onChange={handleSelectCompany}
              placeholder="Buscar por empresa ou CNPJ..."
              isSearchable
              menuPortalTarget={document.body}
              styles={selectStyles}
              noOptionsMessage={() => 'Nenhuma empresa encontrada'}
              filterOption={(option, inputValue) => {
                // Exclui já selecionadas
                if (
                  selectedCompanies.some((c) => c.value === option.data.value)
                )
                  return false;
                // Filtra pelo texto digitado
                if (!inputValue) return true;
                return option.label
                  .toLowerCase()
                  .includes(inputValue.toLowerCase());
              }}
            />

            {selectedCompanies.length > 0 && (
              <SelectedCompaniesWrapper>
                {selectedCompanies.map((company) => (
                  <CompanyTag key={company.value}>
                    <span>
                      <strong>{company.empresa}</strong>
                      {' — '}
                      {company.cnpj}
                    </span>
                    <RemoveButton
                      onClick={() => handleRemoveCompany(company.value)}
                      title="Remover"
                    >
                      ×
                    </RemoveButton>
                  </CompanyTag>
                ))}
              </SelectedCompaniesWrapper>
            )}
          </div>

          {/* ── Perfil e Permissões ── */}
          <div style={{ marginBottom: '80px' }}>
            <SectionTitle>Perfil e Permissões</SectionTitle>
            <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
              <div style={{ width: isTabletOrMobile ? '100%' : '48%' }}>
                <FieldLabel>Selecione o perfil do usuário *</FieldLabel>
                <Select
                  data-testid="user-form-role-select"
                  onChange={(e) => setRole(e.target.value)}
                  value={
                    Object.keys(roleLevel).filter(
                      (x) => roleLevel[x] !== '0' && roleLevel[x] !== undefined
                    )[0]
                  }
                  style={{ width: '100%' }}
                >
                  <option value="isSpecialist">Especialista</option>
                  <option value="isGeneralist">Geral</option>
                  <option value="isManager">Gerente</option>
                  <option value="isWhiteMartins">White Martins</option>
                  <option value="isAdmin">Admin</option>
                </Select>
              </div>
              <div style={{ width: isTabletOrMobile ? '100%' : '48%' }}>
                <FieldLabel>Perfil da Gestão de Pacientes *</FieldLabel>
                <Select
                  data-testid="user-form-patient-v2-profile-select"
                  aria-label="Perfil da Gestão de Pacientes"
                  value={patientV2ProfileValue}
                  onChange={(e) => setPatientV2ProfileValue(e.target.value)}
                  style={{ width: '100%' }}
                >
                  {PATIENT_V2_PROFILE_OPTIONS.map(({ value, label }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            <div style={{ marginTop: '20px' }}>
              {roleLevel?.isWhiteMartins === '1' && (
                <div
                  style={{
                    marginBottom: '12px',
                    backgroundColor: '#fafafa',
                    padding: '12px',
                    borderRadius: '4px',
                    border: '1px solid #e8e8e8',
                  }}
                >
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '14px',
                      color: '#444',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      data-testid="user-form-can-see-all-cnpjs"
                      type="checkbox"
                      checked={canSeeAllCnpjsValue}
                      onChange={(e) => setCanSeeAllCnpjsValue(e.target.checked)}
                      style={{
                        cursor: 'pointer',
                        width: '16px',
                        height: '16px',
                      }}
                    />
                    Visualizar todos os CNPJs
                  </label>
                </div>
              )}

              <FieldLabel>Acessos na página de Novo Pedido</FieldLabel>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: isTabletOrMobile
                    ? '1fr'
                    : 'repeat(2, minmax(0, 1fr))',
                  gap: '12px 20px',
                  backgroundColor: '#fafafa',
                  padding: '12px',
                  borderRadius: '4px',
                  border: '1px solid #e8e8e8',
                }}
              >
                {[
                  { key: 'implantacao', label: 'Implantação' },
                  { key: 'assistencia', label: 'Assistência Técnica' },
                  { key: 'recarga', label: 'Recarga' },
                  { key: 'recolhimento', label: 'Recolhimento' },
                  { key: 'venda', label: 'Descartáveis' },
                  { key: 'subalocacao', label: 'Sublocação' },
                ].map((perm) => (
                  <label
                    key={perm.key}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '14px',
                      color: '#444',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      data-testid={`user-form-access-${perm.key}`}
                      type="checkbox"
                      checked={permissions[perm.key]}
                      onChange={(e) =>
                        setPermissions({
                          ...permissions,
                          [perm.key]: e.target.checked,
                        })
                      }
                      style={{
                        cursor: 'pointer',
                        width: '16px',
                        height: '16px',
                      }}
                    />
                    {perm.label}
                  </label>
                ))}
              </div>

              <div style={{ marginTop: '16px' }}>
                <FieldLabel>Permissão de acesso ao relatório</FieldLabel>
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr',
                  gap: '12px 20px',
                  backgroundColor: '#fafafa',
                  padding: '12px',
                  borderRadius: '4px',
                  border: '1px solid #e8e8e8',
                }}
              >
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    color: '#444',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={permissions.report}
                    onChange={(e) =>
                      setPermissions({
                        ...permissions,
                        report: e.target.checked,
                      })
                    }
                    style={{
                      cursor: 'pointer',
                      width: '16px',
                      height: '16px',
                    }}
                  />
                  Relatórios
                </label>
              </div>

              {(roleLevel?.isManager === '1' ||
                roleLevel?.isWhiteMartins === '1' ||
                roleLevel?.isAdmin === '1') && (
                <>
                  <div style={{ marginTop: '16px' }}>
                    <FieldLabel>Permissão em pacientes</FieldLabel>
                  </div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr',
                      gap: '12px 20px',
                      backgroundColor: '#fafafa',
                      padding: '12px',
                      borderRadius: '4px',
                      border: '1px solid #e8e8e8',
                    }}
                  >
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: '14px',
                        color: '#444',
                        cursor:
                          roleLevel?.isAdmin === '1'
                            ? 'not-allowed'
                            : 'pointer',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={
                          roleLevel?.isAdmin === '1' || permissions.paymentList
                        }
                        disabled={roleLevel?.isAdmin === '1'}
                        onChange={(e) =>
                          setPermissions({
                            ...permissions,
                            paymentList: e.target.checked,
                          })
                        }
                        style={{
                          cursor:
                            roleLevel?.isAdmin === '1'
                              ? 'not-allowed'
                              : 'pointer',
                          width: '16px',
                          height: '16px',
                        }}
                      />
                      Editar lista de pagamento de pacientes
                    </label>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* ── Botões fixos ── */}
        <div
          style={{
            display: 'flex',
            paddingTop: '15px',
            paddingBottom: '5px',
            borderTop: '1px solid #e8e8e8',
            justifyContent: 'space-between',
            gap: '10px',
            backgroundColor: 'white',
            flexShrink: 0,
          }}
        >
          <SecondaryButton
            style={{ width: '140px', height: '42px' }}
            onClick={onCancel ?? onAction}
          >
            Cancelar
          </SecondaryButton>
          <PrimaryButton
            data-testid="user-form-submit"
            style={{ width: '140px', height: '42px' }}
            onClick={saveUser}
          >
            {isEditing ? 'Atualizar' : 'Criar Usuário'}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};
